import MDBox from "components/MDBox"
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { useState, useEffect } from "react";

export default function ProductDetailsFields({
    changeHandler,
    errors,
    product
}) {
    const [localProduct, setLocalProduct] = useState({});

    useEffect(() => {
        if (Object.keys(product).length === 0) {
            handleChange({ target: { name: "shelfLifeUnit", value: localProduct["shelfLifeUnit"] || "months" } }); // default val
        } else {
            let initProduct = { ...product };
            handleChange({ target: { name: "shelfLifeUnit", value: initProduct.shelfLifeUnit || "months" } }); // default val
            // Normalize initial values and convert dateOfStorage to Dayjs
            const adapter = new AdapterDayjs();

            if (initProduct.dateOfStorage) {
                initProduct.dateOfStorage = adapter.date(initProduct.dateOfStorage);
            } else {
                initProduct.dateOfStorage = null;
            }

            if (!initProduct.shelfLifeUnit) {
                initProduct.shelfLifeUnit = initProduct.shelfLife[1];
                initProduct.shelfLife = initProduct.shelfLife[0];
            }
            setLocalProduct(initProduct);
        }
    }, [])

    const commonProps = { // Props common for the input fields
        display: "flex",
        flexDirection: "column",
        fullWidth: true,
        marginTop: "0.3rem",
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const updatedProduct = { ...localProduct, [name]: value };
        setLocalProduct(updatedProduct);
        changeHandler(name, value);
    }

    return (
        <MDBox mt={2}>
            <Grid container spacing={2}>
                {/*S/N*/}
                <Grid item xs={12} md={6}>
                    <MDBox {...commonProps}>
                        <TextField
                            label="S/N"
                            variant="outlined"
                            name="serialNumber"
                            value={localProduct.serialNumber || ""}
                            onChange={handleChange}
                            error={errors.serialNumber}
                            helperText={errors.serialNumber}
                            fullWidth
                        />
                    </MDBox>
                </Grid>

                {/* MAC Address */}
                <Grid item xs={12} md={6}>
                    <MDBox {...commonProps}>
                        <TextField
                            label="MAC Address"
                            variant="outlined"
                            name="macAddress"
                            value={localProduct.macAddress || ""}
                            onChange={handleChange}
                            error={errors.macAddress}
                            helperText={errors.macAddress}
                            fullWidth
                        />
                    </MDBox>
                </Grid>

                {/* Date of storage */}
                <Grid item xs={12} md={4}>
                    <MDBox {...commonProps}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Date of Storage"
                                value={localProduct.dateOfStorage || null}
                                onChange={(newValue) => {
                                    handleChange({ target: { name: "dateOfStorage", value: newValue } });
                                }}
                                slotProps={{
                                    textField: {
                                        error: errors.dateOfStorage,
                                        helperText: errors.dateOfStorage,
                                    }
                                }}
                                helperText={errors.dateOfStorage}
                            />
                        </LocalizationProvider>
                    </MDBox>
                </Grid>

                {/* Firmware Version */}
                <Grid item xs={12} md={4}>
                    <MDBox {...commonProps}>
                        <TextField
                            label="Firmware Version"
                            variant="outlined"
                            name="firmwareVersion"
                            value={localProduct.firmwareVersion || ""}
                            onChange={handleChange}
                            error={errors.firmwareVersion}
                            fullWidth
                            helperText={errors.firmwareVersion}
                        />
                    </MDBox>
                </Grid>

                {/* Battery Capacity */}
                <Grid item xs={12} md={4}>
                    <MDBox {...commonProps}>
                        <TextField
                            label="Battery Capacity (mAh)"
                            type="number"
                            variant="outlined"
                            name="batteryCapacity"
                            value={localProduct.batteryCapacity || ""}
                            onChange={handleChange}
                            error={errors.batteryCapacity}
                            helperText={errors.batteryCapacity}
                            fullWidth
                        />
                    </MDBox>
                </Grid>

                {/* Shelf Life */}
                <Grid item xs={12} md={6}>
                    <MDBox display="flex">
                        <MDBox {...commonProps} width="75%">
                            <TextField
                                label="Shelf life"
                                type="number"
                                variant="outlined"
                                name="shelfLife"
                                value={localProduct.shelfLife || ""}
                                onChange={handleChange}
                                error={errors.shelfLife}
                                helperText={errors.shelfLife}
                                fullWidth
                            />
                        </MDBox>
                        <MDBox {...commonProps} width="25%" ml={1}>
                            <FormControl fullWidth>
                                <InputLabel id="shelf-life-unit-label">Unit</InputLabel>
                                <Select
                                    label="Unit"
                                    labelId="shelf-life-unit-label"
                                    variant="outlined"
                                    name="shelfLifeUnit"
                                    value={localProduct.shelfLifeUnit || "months"}
                                    onChange={handleChange}
                                    fullWidth
                                    sx={{ height: '45px', maxWidth: '100%', '& .MuiSelect-select': { paddingY: '8px' } }}
                                >
                                    <MenuItem value="days">Days</MenuItem>
                                    <MenuItem value="months">Months</MenuItem>
                                    <MenuItem value="years">Years</MenuItem>
                                </Select>
                            </FormControl>
                        </MDBox>
                    </MDBox>
                </Grid>

                {/* Price */}
                <Grid item xs={12} md={6}>
                    <MDBox display="flex">
                        <MDBox {...commonProps} width="75%">
                            <TextField
                                label="Price"
                                type="number"
                                variant="outlined"
                                inputProps={{ step: "0.01" }}
                                name="price"
                                value={localProduct.price || ""}
                                onChange={handleChange}
                                error={errors.price}
                                helperText={errors.price}
                                fullWidth
                            />
                        </MDBox>
                        <MDBox {...commonProps} width="25%" ml={1}>
                            <FormControl fullWidth>
                                <InputLabel id="currency-label">Currency</InputLabel>
                                <Select
                                    label="Currency"
                                    labelId="currency-label"
                                    variant="outlined"
                                    name="currency"
                                    value={localProduct.currency || "CAD"}
                                    onChange={handleChange}
                                    fullWidth
                                    sx={{ height: '45px', maxWidth: '100%', '& .MuiSelect-select': { paddingY: '8px' } }}
                                >
                                    <MenuItem value="CAD">CAD</MenuItem>
                                    <MenuItem value="USD">USD</MenuItem>
                                    <MenuItem value="EUR">EUR</MenuItem>
                                    <MenuItem value="GBP">GBP</MenuItem>
                                </Select>
                            </FormControl>
                        </MDBox>
                    </MDBox>
                </Grid>

            </Grid>
        </MDBox>
    )
}